import { Button, Pagination, Search } from '@ads/components-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/shared/ui/atoms';
import { useClassroomList } from '../../../hooks/useClassroomList';
import { ClassroomCheckboxItem } from '../../atoms/ClassroomCheckboxItem';

const DEFAULT_PAGE_SIZE = 8;

export interface ClassroomListProps {
  readonly selectedIds: readonly string[];
  readonly onToggle: (id: string, studentCount: number, name: string) => void;
  readonly onSelectPage: (
    classrooms: ReadonlyArray<{
      id: string;
      studentCount: number;
      name: string;
    }>,
  ) => void;
  readonly pageSize?: number;
}

/**
 * Organismo de listagem paginada de turmas com busca server-side e seleção por página.
 * Gerencia internamente paginação e busca via `useClassroomList`.
 *
 * @param selectedIds - IDs das turmas atualmente selecionadas
 * @param onToggle - Callback ao selecionar/desselecionar uma turma individualmente
 * @param onSelectPage - Callback ao clicar em "Selecionar todos" / "Limpar seleção" da página atual
 * @param pageSize - Itens por página (padrão: 8)
 */
export function ClassroomList({
  selectedIds,
  onToggle,
  onSelectPage,
  pageSize = DEFAULT_PAGE_SIZE,
}: ClassroomListProps) {
  const { t } = useTranslation('notification');

  const { classrooms, pageIndex, setPageIndex, totalPages, search, setSearch, isLoading } =
    useClassroomList(pageSize);

  const pageIds = classrooms.map(c => c.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

  return (
    <div className="flex flex-col border-hairline border-ctx-layout-border rounded-md">
      <div className="flex items-center justify-between gap-sm px-xxs py-micro border-b-hairline border-ctx-layout-border">
        <span className="ts-subtitle-xs shrink-0 text-ctx-content-title">
          {t('wizard.audience.classrooms')}
        </span>
        <div className="w-full max-w-[420px]">
          <Search
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            placeholder={t('wizard.audience.classroomSearchPlaceholder')}
            size="md"
          />
        </div>
      </div>

      <div className="flex items-center justify-between px-xxs py-micro border-b-hairline border-ctx-layout-border">
        <span className="ts-paragraph-xxxs text-ctx-content-base">
          {t('wizard.audience.selectedCount', { count: selectedIds.length })}
          {' · '}
          {t('wizard.audience.existingCount', { count: classrooms.length })}
        </span>
        <Button hierarchy="secondary" size="sm" onClick={() => onSelectPage(classrooms)}>
          {allPageSelected ? t('wizard.audience.clearSelection') : t('wizard.audience.selectAll')}
        </Button>
      </div>

      <div className="flex flex-col">
        {isLoading
          ? Array.from({ length: pageSize }).map((_, i) => (
              <div
                key={i}
                className="flex items-center gap-mini px-xxs py-nano border-b-hairline border-ctx-layout-border last:border-b-none"
              >
                <Skeleton className="size-mini shrink-0" />
                <Skeleton className="h-micro flex-1" />
                <Skeleton className="h-xxxs w-[4rem] shrink-0" />
              </div>
            ))
          : classrooms.map(classroom => (
              <ClassroomCheckboxItem
                key={classroom.id}
                classroom={classroom}
                checked={selectedIds.includes(classroom.id)}
                onToggle={onToggle}
              />
            ))}
      </div>

      <div className="flex justify-center py-nano">
        <Pagination
          pageAmount={totalPages}
          activeIndex={pageIndex}
          setActiveIndex={setPageIndex}
          ellipsisLabel="..."
        />
      </div>
    </div>
  );
}
